import { useEffect, useState } from 'react'
import { Pencil } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import { eventMeta, type MaintenanceTask } from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import {
  REPEAT_CYCLES,
  REPEAT_CYCLE_LABELS,
  type RepeatCycle,
  type UpdateEventPayload,
} from '@/api/equipmentApi'

const UNASSIGNED = 'none'
// Radix Select cannot hold an empty-string value, so "does not repeat" needs a
// sentinel of its own.
const NO_REPEAT = 'no-repeat'
const PRIORITIES = ['1', '2', '3', '4', '5']

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='space-y-1'>
      <label className='text-xs font-medium text-gray-600'>{label}</label>
      {children}
    </div>
  )
}

/** Edit an event's schedulable/assignable fields (Tasks-tab Edit action). */
export function EditEventModal({
  task,
  onClose,
  onSave,
}: {
  task: MaintenanceTask | null
  onClose: () => void
  onSave: (id: string, patch: UpdateEventPayload) => void
}) {
  const { users } = useAssignableUsers()
  // The tenant's name for the type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const evt = task ? eventMeta(task.eventType, catalog) : null

  const [priority, setPriority] = useState('3')
  const [dueDate, setDueDate] = useState('')
  const [earlyStart, setEarlyStart] = useState('')
  const [planHours, setPlanHours] = useState('')
  const [assignee, setAssignee] = useState(UNASSIGNED)
  const [sop, setSop] = useState('')
  const [usedBy, setUsedBy] = useState('')
  const [repeatCycle, setRepeatCycle] = useState<RepeatCycle | ''>('')

  useEffect(() => {
    if (!task) return
    setPriority(task.priority === null ? '' : String(task.priority))
    setDueDate(task.dueDate)
    setEarlyStart(task.earlyStart ?? '')
    setPlanHours(String(task.planHours))
    setAssignee(task.assigneeId ?? UNASSIGNED)
    setSop(task.sopRef ?? '')
    setUsedBy(task.usedBy ?? '')
    setRepeatCycle((task.repeatCycle as RepeatCycle | undefined) ?? '')
  }, [task])

  const save = () => {
    if (!task) return
    const patch: UpdateEventPayload = {}
    if (dueDate && dueDate !== task.dueDate) patch.dueDate = dueDate
    if (earlyStart && earlyStart !== (task.earlyStart ?? '')) patch.earlyStart = earlyStart
    if (priority !== '' && Number(priority) !== task.priority) patch.priority = Number(priority)
    if (planHours !== '' && Number(planHours) !== task.planHours) patch.planHours = Number(planHours)
    if (sop !== (task.sopRef ?? '')) patch.sopRef = sop
    if (usedBy !== task.usedBy) patch.usedBy = usedBy
    const newAssignee = assignee === UNASSIGNED ? '' : assignee
    if (newAssignee !== (task.assigneeId ?? '')) patch.assigneeId = newAssignee
    // '' stops the repeat without deleting the task — the only way to end one
    // short of completing the work.
    if (repeatCycle !== (task.repeatCycle ?? '')) patch.repeatCycle = repeatCycle
    onSave(task.id, patch)
    onClose()
  }

  return (
    <Dialog open={task !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-lg'>
        {task && evt && (
          <>
            <div className='flex items-start gap-3 border-b border-gray-100 bg-gray-50/60 px-6 py-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
                <Pencil className='h-5 w-5' />
              </div>
              <div>
                <h2 className='text-lg font-semibold tracking-tight text-gray-900'>Edit Event</h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  {task.equipmentId} · {evt.label.replace(/ \(.*\)$/, '')}
                </p>
              </div>
            </div>

            <div className='space-y-4 px-6 py-5'>
              <div className='grid gap-3 md:grid-cols-3'>
                <Field label='Priority'>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label='Planned Hrs'>
                  <Input type='number' step='0.5' min='0' value={planHours} onChange={(e) => setPlanHours(e.target.value)} />
                </Field>
                <Field label='Assign To'>
                  <Select value={assignee} onValueChange={setAssignee}>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className='grid gap-3 md:grid-cols-2'>
                <Field label='Early Start Date'>
                  <Input type='date' value={earlyStart} onChange={(e) => setEarlyStart(e.target.value)} />
                </Field>
                <Field label='Due Date'>
                  <Input type='date' value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </Field>
              </div>

              {/* A task on a material cannot repeat: it paints on no unit's calendar (z8nrz7fjzc). */}
              {task.taskClass === 'preventive' && !task.material && (
                <div className='grid gap-3 md:grid-cols-2'>
                  <Field label='Repeats'>
                    <Select
                      value={repeatCycle === '' ? NO_REPEAT : repeatCycle}
                      onValueChange={(v) =>
                        setRepeatCycle(v === NO_REPEAT ? '' : (v as RepeatCycle))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Does not repeat' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_REPEAT}>Does not repeat</SelectItem>
                        {REPEAT_CYCLES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {REPEAT_CYCLE_LABELS[c]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className='mt-1 text-[11px] text-muted-foreground'>
                      {repeatCycle === ''
                        ? 'Shown on the calendar on its due date only.'
                        : 'One task, shown on the calendar from the early start date to the due date — or until it is completed. Set to “Does not repeat” to stop it without deleting the task.'}
                    </p>
                  </Field>
                </div>
              )}

              <div className='grid gap-3 md:grid-cols-2'>
                <Field label='SOP'>
                  <Input value={sop} onChange={(e) => setSop(e.target.value)} placeholder='e.g. SOP-CAL-014' />
                </Field>
                <Field label='Used By'>
                  <Input value={usedBy} onChange={(e) => setUsedBy(e.target.value)} placeholder='Process area' />
                </Field>
              </div>
            </div>

            <div className='flex items-center gap-3 border-t border-gray-100 bg-gray-50/60 px-6 py-4'>
              <button
                type='button'
                onClick={onClose}
                className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={save}
                className='ml-auto inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98]'
              >
                <Pencil className='h-4 w-4' />
                Save Changes
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
